import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Package, Edit2, Eye, Trash2, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { productService, clientService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'obsolete', label: 'Obsolete' },
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    client_id: '',
    material: '',
    raw_material_code: '',
    min_stock_grezzo: '',
    external_treatment: '',
    unit_of_measure: 'pz',
    product_family: '',
    technical_notes: '',
    unit_price: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [search, filterClient, filterStatus]);

  const loadClients = async () => {
    try {
      const response = await clientService.getAll({ is_active: true });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const loadProducts = async () => {
    try {
      const params = { search };
      if (filterClient) params.client_id = filterClient;
      if (filterStatus) params.status = filterStatus;
      const response = await productService.getAll(params);
      setProducts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento prodotti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.client_id) delete data.client_id;
      data.unit_price = data.unit_price !== '' ? parseFloat(data.unit_price) : null;
      data.min_stock_grezzo = data.min_stock_grezzo !== '' ? parseFloat(data.min_stock_grezzo) : null;
      
      if (editingProduct) {
        await productService.update(editingProduct.id, data);
        toast.success('Prodotto aggiornato');
      } else {
        await productService.create(data);
        toast.success('Prodotto creato');
      }
      setDialogOpen(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operazione fallita');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code,
      name: product.name,
      description: product.description || '',
      client_id: product.client_id || '',
      material: product.material || '',
      raw_material_code: product.raw_material_code || '',
      min_stock_grezzo: product.min_stock_grezzo != null ? product.min_stock_grezzo : '',
      external_treatment: product.external_treatment || '',
      unit_of_measure: product.unit_of_measure || 'pz',
      product_family: product.product_family || '',
      technical_notes: product.technical_notes || '',
      unit_price: product.unit_price != null ? product.unit_price : ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (product) => {
    if (window.confirm(`Are you sure you want to set ${product.code} to obsolete?`)) {
      try {
        await productService.delete(product.id);
        toast.success('Prodotto impostato come obsoleto');
        loadProducts();
      } catch (error) {
        toast.error('Failed to update product');
      }
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      client_id: '',
      material: '',
      raw_material_code: '',
      external_treatment: '',
      unit_of_measure: 'pz',
      product_family: '',
      technical_notes: ''
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500 text-white',
      suspended: 'bg-amber-500 text-white',
      obsolete: 'bg-slate-400 text-white',
    };
    return styles[status] || styles.active;
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Prodotti</h1>
          <p className="text-sm text-slate-500 mt-1">Gestisci l'anagrafica prodotti</p>
        </div>
        {hasRole('admin', 'quality', 'production') && (
          <Button onClick={openNewDialog} className="h-12 px-6 text-base" data-testid="add-product-btn">
            <Plus className="w-5 h-5 mr-2" />
            Aggiungi Prodotto
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per codice o nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-products-input"
              />
            </div>
            <Select value={filterClient || "all"} onValueChange={(v) => setFilterClient(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No products found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Nome</th>
                    <th>Cliente</th>
                    <th>Materiale</th>
                    <th>Famiglia</th>
                    <th>Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="row-link" onClick={() => navigate(`/products/${product.id}`)} data-testid={`product-row-${product.id}`}>
                      <td className="font-mono font-medium">{product.code}</td>
                      <td className="font-medium">{product.name}</td>
                      <td>{product.client_name || '-'}</td>
                      <td className="text-slate-600">{product.material || '-'}</td>
                      <td className="text-slate-600">{product.product_family || '-'}</td>
                      <td>
                        <Badge className={getStatusBadge(product.status)}>
                          {product.status}
                        </Badge>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {hasRole('admin', 'quality', 'production') && (
                            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {hasRole('admin') && (
                            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleDelete(product)}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="code">Product Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  disabled={!!editingProduct}
                  data-testid="product-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_id">Client</Label>
                <Select
                  value={formData.client_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_family">Product Family</Label>
                <Input
                  id="product_family"
                  value={formData.product_family}
                  onChange={(e) => setFormData({ ...formData, product_family: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="material">Material</Label>
                <Input
                  id="material"
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="raw_material_code">Codice grezzo</Label>
                <Input
                  id="raw_material_code"
                  value={formData.raw_material_code}
                  onChange={(e) => setFormData({ ...formData, raw_material_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_stock_grezzo">Soglia minima grezzo</Label>
                <Input
                  id="min_stock_grezzo"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Es: 100"
                  value={formData.min_stock_grezzo}
                  onChange={(e) => setFormData({ ...formData, min_stock_grezzo: e.target.value })}
                  data-testid="input-min-stock-grezzo"
                />
                <p className="text-[10px] text-slate-500">Se la qtà libera scende sotto la soglia, arriva un alert email settimanale.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="external_treatment">External Treatment</Label>
                <Input
                  id="external_treatment"
                  value={formData.external_treatment}
                  onChange={(e) => setFormData({ ...formData, external_treatment: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit_of_measure">Unit of Measure</Label>
                <Input
                  id="unit_of_measure"
                  value={formData.unit_of_measure}
                  onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="technical_notes">Note Tecniche</Label>
                <Textarea
                  id="technical_notes"
                  value={formData.technical_notes}
                  onChange={(e) => setFormData({ ...formData, technical_notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit_price">Prezzo Unitario (EUR)</Label>
                <Input
                  id="unit_price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  data-testid="product-unit-price"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="h-11 px-5" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="h-11 px-6" data-testid="save-product-btn">
                {editingProduct ? 'Aggiorna' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
